import {
  clearStorage,
  getSessionCookies,
  setSessionCookies,
} from "@/lib/cookies";
import { funifierApi } from "@/services/api";
import { signIn } from "@/services/auth";
import { getPresets } from "@/services/settings";
import { refreshUserStatus } from "@/services/user";
import { AppSession, LoginCredentials } from "@/types/auth";
import { MenuItem } from "@/types/settings";
import { User } from "@/types/user";
import type {} from "@redux-devtools/extension"; // required for devtools typing
import { create } from "zustand";
import { devtools } from "zustand/middleware";

// Define types for our store state
interface AppStore {
  appSession: AppSession | undefined;
  user: User | undefined;
  menu: MenuItem[] | undefined;
  presets: any;
  logout: () => void;
  isLoggedIn: () => boolean;
  setAppSession: () => void;
  setUser: (user: User) => void;
  refreshUser: () => Promise<void>;
  loginWithToken: (access_token: string) => Promise<User | undefined>;
  login: ({ username, password }: LoginCredentials) => Promise<boolean>;
  restoreSession: () => Promise<void>;
  loadPresets: () => Promise<void>;
  validateAccess: () => Promise<boolean>;
}
export const useAppStore = create<AppStore>()(
  devtools((set, get) => ({
    user: undefined,
    appSession: undefined,
    menu: undefined,
    presets: undefined,
    isLoggedIn: () => !!get().user,
    setAppSession: () => {
      const session = getSessionCookies();
      if (session) {
        setSessionCookies(session);
      }
    },
    setUser: (user) => set({ user }),
    refreshUser: async () => {
      const user = await refreshUserStatus();
      window.postMessage("FUNIFIER_UPDATE", "*");
      set({ user });
    },
    loginWithToken: async (access_token) => {
      console.log("loginWithToken", access_token);
      return undefined;
    },
    login: async ({ username, password }) => {
      const session = await signIn({ username, password });
      if (session) {
        setSessionCookies(session);
        return true;
      }
      return false;
    },
    logout: () => {
      clearStorage();
      set({ user: undefined, appSession: undefined });
    },
    restoreSession: async () => {
      const session = getSessionCookies();
      if (!get().appSession && session) {
        set({ appSession: session });
        if (!get().user) {
          const user = await refreshUserStatus();
          set({ user });
        }
      }
    },
    loadPresets: async () => {
      const result = await getPresets();
      const presets = result.reduce((acc: any, item: any) => {
        acc[item._id] = item;
        delete acc[item._id]._id;
        return acc;
      }, {});
      set({ presets });
    },
    validateAccess: async () => {
      const { user, presets } = get();
      if (user && presets.access_control) {
        console.log("validateAccess", presets.access_control);
        const payload: any[] = [];

        if (presets.access_control.contentId) {
          if (presets.access_control.type === "action_log") {
            payload.push({
              $match: {
                userId: user._id,
                actionId: presets.access_control.contentId,
              },
            });
          } else {
            payload.push({
              $match: {
                _id: presets.access_control.contentId,
              },
            });
          }
        }

        if (presets.access_control.time) {
          payload.push({
            $match: {
              time: {
                $gte: {
                  $date: presets.access_control.time,
                },
              },
            },
          });
        }

        const { data } = await funifierApi.post(
          `database/${presets.access_control.type}/aggregate?strict=true`,
          payload,
        );

        return data.length === 0;

        // switch (presets.access_control.type) {
        //   case "action_log":
        //     return false;
        //   default:
        //     return true;
        // }
      }
      return false;
    },
  })),
);
